<script lang="ts">
  import { onMount } from "svelte";
  import { navigate } from "../../app.logic";
  import { isUserRoleADM } from "../../app.store";
  import ActivityTypeDropdown from "../../components/Business/ActivityTypeDropdown.svelte";
  import CustomerDropdown from "../../components/Business/CustomerDropdown.svelte";
  import DossierDropdown from "../../components/Business/DossierDropdown.svelte";
  import PortalUserDropdown from "../../components/Business/PortalUserDropdown.svelte";
  import DateInput from "../../components/Form/Fields/DateInput.svelte";
  import Input from "../../components/Form/Fields/Input.svelte";
  import NumberInput from "../../components/Form/Fields/NumberInput.svelte";
  import Switch from "../../components/Form/Fields/Switch.svelte";
  import Textarea from "../../components/Form/Fields/Textarea.svelte";
  import TimeInput from "../../components/Form/Fields/TimeInput.svelte";
  import FormField from "../../components/Form/FormField.svelte";
  import type { IActivityType } from "../../entities/activity-type";
  import type { IDossier } from "../../entities/dossier";
  import FormLayout from "../../layout/FormLayout.svelte";
  import MainLayout from "../../layout/MainLayout.svelte";
  import { ActivityTypesService } from "../../services/activity-types.service";
  import { DossierService } from "../../services/dossier.service";
  import type { IDropdownItem } from "../../services/DTO/dropdown-item";
  import type { IPortalUserDTO } from "../../services/DTO/portal-user-dto";
  import { PortalUserService } from "../../services/portal-user.service";
  import type { IApiResponse } from "../../utils/api/api-response";
  import {
    initDetail,
    record,
    save,
    dossier,
    applyUserRevenue,
  } from "./activity.store";

  export let oid: number;

  $: isNew = oid == 0;
  $: canSubmit =
    $record != null &&
    $record.activityTypeId != null &&
    ($record.hours > 0 || $record.minutes > 0);

  async function onSubmit() {
    let newP = isNew;
    let u = await save();
    if (newP) {
      oid = u.oid;
      record.set(u);
      navigate(`/activity/${u.oid}`);
    } else {
      onCancel();
    }
  }

  function onCancel() {
    navigate(`/activity`);
  }

  async function onDossierSelect(event: CustomEvent) {
    let item: IDropdownItem = event.detail;
    let _resp: IApiResponse<IDossier> = await DossierService.fetchDossier(
      item.oid
    );
    let _dossier = _resp.payload;
    dossier.set(_dossier);
    if (_dossier != null && _dossier.customerId != $record.customerId) {
      $record.customerId = _dossier.customerId;
    }
  }

  async function onActivityTypeSelect(event: CustomEvent) {
    let item: IDropdownItem = event.detail;
    if ($applyUserRevenue == false) {
      let _resp: IApiResponse<IActivityType> =
        await ActivityTypesService.fetchActivityType(item.oid);
      let _at = _resp.payload;
      if (_at != null && $record != null) {
        $record.hourlyRevenue = _at.hourlyRevenue;
      }
    }
  }

  async function onPortalUserSelect(event: CustomEvent) {
    let item: IDropdownItem = event.detail;
    let _resp: IApiResponse<IPortalUserDTO> =
      await PortalUserService.fetchPortalUser(item.oid);
    let _us = _resp.payload;
    if (_us != null && $record != null) {
      $record.hourlyLaborCost = _us.hourlyCost;
      if ($applyUserRevenue == true) {
        $record.hourlyRevenue = _us.hourlyRevenue;
      }
    }
  }

  onMount(() => initDetail(oid));
</script>

<MainLayout>
  {#if $record}
    <FormLayout {canSubmit} {isNew} on:submit={onSubmit} on:cancel={onCancel}>
      <div class="columns">
        <div class="column is-8">
          <FormField>
            <CustomerDropdown
              name="Cliente"
              required={false}
              bind:value={$record.customerId}
              on:select={($record.dossierId = null)}
            />
          </FormField>
        </div>

        <div class="column is-4">
          <FormField>
            <DateInput
              name="Data"
              placeholder="data"
              required={true}
              bind:value={$record.date}
            />
          </FormField>
        </div>
      </div>

      <div class="columns">
        <div class="column">
          <FormField>
            <ActivityTypeDropdown
              name="Causale Attività"
              required={true}
              dossierTypeId={$dossier != null ? $dossier.dossierTypeId : 0}
              bind:value={$record.activityTypeId}
              on:select={onActivityTypeSelect}
            />
          </FormField>
        </div>
        <div class="column">
          <FormField>
            <DossierDropdown
              name="Pratica"
              required={false}
              customerId={$record.customerId || 0}
              bind:value={$record.dossierId}
              on:select={onDossierSelect}
            />
          </FormField>
        </div>
      </div>

      <div class="columns">
        <div class="column">
          <FormField>
            <Input
              name="Descrizione aggiuntiva"
              placeholder="descrizione aggiuntiva"
              required={false}
              bind:value={$record.title}
            />
          </FormField>
        </div>
        <div class="column">
          <FormField>
            <PortalUserDropdown
              name="Utente"
              required={true}
              disabled={$isUserRoleADM == false}
              bind:value={$record.portalUserId}
              on:select={onPortalUserSelect}
            />
          </FormField>
        </div>
      </div>

      <div class="columns">
        <div class="column">
          <FormField>
            <TimeInput
              name="Tempo (Ore : Minuti)"
              required={true}
              bind:hours={$record.hours}
              bind:minutes={$record.minutes}
            />
          </FormField>
        </div>
        <div class="column">
          <FormField>
            <NumberInput
              name="Ricavo orario"
              placeholder="ricavo orario"
              required={true}
              bind:value={$record.hourlyRevenue}
            />
          </FormField>
        </div>
        <div class="column">
          {#if $isUserRoleADM}
            <FormField>
              <NumberInput
                name="Costo orario"
                placeholder="costo orario"
                required={true}
                bind:value={$record.hourlyLaborCost}
              />
            </FormField>
          {/if}
        </div>
        <div class="column">
          <FormField klass="has-text-centered">
            <Switch
              name="Costi"
              bind:value={$record.costs}
              on:toggle={(_) => ($record.costsAmount = 0)}
            />
          </FormField>
        </div>
        <div class="column">
          <FormField>
            <NumberInput
              name="Costi anticipati"
              placeholder="costi anticipati"
              disabled={$record.costs == 0}
              required={false}
              bind:value={$record.costsAmount}
            />
          </FormField>
        </div>

        <div class="column">
          <FormField klass="has-text-centered">
            <Switch name="Attività Fatturata" bind:value={$record.billed} />
          </FormField>
        </div>
      </div>

      <div class="columns">
        <div class="column">
          <FormField>
            <Textarea
              name="Note"
              placeholder="note"
              required={false}
              bind:value={$record.notes}
            />
          </FormField>
        </div>
      </div>
    </FormLayout>
  {/if}
</MainLayout>
